import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authAPI, User, UserAccountRole } from '../api/authApi';
import { impersonateUserByUid } from '../auth/impersonateUser';
import { joinCsvRow } from '@/utils/csvExport';
import { MoreHorizontal, Pencil, Sliders, Trash2, Download, ChevronDown, UserCog, Search, UserPlus, Loader2, EyeOff } from 'lucide-react';
import { searchInputOverlayIconClass } from '@/lib/searchInputIcon';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import ExcelJS from 'exceljs';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { dataAPI } from '../api/dataApi';
import { updateItemAPI, deleteItemAPI } from '../api/itemsApi';

import { resolveApiBaseUrl } from '../api/apiBase';
import { UiSettingsFields } from '../user/UiSettingsFields';
import { uiSettingsFromUser, type UiSettingsFormValue } from '../user/uiSettings';
import { UserTypeField } from '../user/UserTypeField';
import { readUserType, type UserType } from '../user/userType';

const API_BASE_URL = resolveApiBaseUrl();
const USERS_SLUG = 'users';

const USER_ACCOUNT_ROLES: UserAccountRole[] = ['admin', 'user'];

function resolvePeerAccountRole(u: Record<string, unknown>): UserAccountRole {
    const info = u.info as { role?: string; admin?: boolean } | undefined;
    const r = u.role ?? info?.role;
    if (typeof r === 'string') {
        const x = r.trim().toLowerCase();
        if (x === 'member' || x === 'visitor') return 'user';
        if (USER_ACCOUNT_ROLES.includes(x as UserAccountRole)) return x as UserAccountRole;
    }
    if (u.admin === true || info?.admin === true) return 'admin';
    return 'user';
}

function getUserDisplayName(targetUser: User): string {
    return targetUser.info?.name ||
        (targetUser.firstName || targetUser.lastName
            ? [targetUser.firstName, targetUser.lastName].filter(Boolean).join(' ')
            : '') ||
        targetUser.uid ||
        'Unknown User';
}

function userMatchesSearch(targetUser: User, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const role = resolvePeerAccountRole({
        role: targetUser.role,
        admin: targetUser.admin,
        info: targetUser.info,
    } as Record<string, unknown>);
    const haystack = [
        getUserDisplayName(targetUser),
        targetUser.email ?? '',
        targetUser.organization ?? '',
        targetUser.uid ?? '',
        role,
        targetUser.active ? 'active' : 'inactive',
    ].join(' ').toLowerCase();
    return haystack.includes(q);
}

const AdminUsersPage: React.FC = () => {
    const { user, refreshUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [users, setUsers] = useState<User[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [limitDialogOpen, setLimitDialogOpen] = useState(false);
    const [selectedUserForLimit, setSelectedUserForLimit] = useState<User | null>(null);
    const [collectionLimitInput, setCollectionLimitInput] = useState<string>('');
    const [isUpdatingLimit, setIsUpdatingLimit] = useState(false);
    const [uiDialogOpen, setUiDialogOpen] = useState(false);
    const [selectedUserForUi, setSelectedUserForUi] = useState<User | null>(null);
    const [uiSettingsForm, setUiSettingsForm] = useState<UiSettingsFormValue>({
        hideInbox: false,
        hideActions: false,
        hideSubmissions: false,
    });
    const [userTypeForm, setUserTypeForm] = useState<UserType>('submitter');
    const [isUpdatingUi, setIsUpdatingUi] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [userToEdit, setUserToEdit] = useState<User | null>(null);
    const [editForm, setEditForm] = useState<{
        firstName: string;
        lastName: string;
        email: string;
        organization: string;
        active: boolean;
        role: UserAccountRole;
    }>({ firstName: '', lastName: '', email: '', organization: '', active: true, role: 'user' });
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createForm, setCreateForm] = useState<{
        firstName: string;
        lastName: string;
        email: string;
        organization: string;
        active: boolean;
        role: UserAccountRole;
    }>({ firstName: '', lastName: '', email: '', organization: '', active: true, role: 'user' });
    const [isSavingCreate, setIsSavingCreate] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportParams, setExportParams] = useState<{ scope: 'all' | 'selection'; format: 'json' | 'xlsx' | 'txt' | 'semicolon' | 'csv' } | null>(null);
    const [exportAllFields, setExportAllFields] = useState(true);
    const [exportSelectedFields, setExportSelectedFields] = useState<string[]>([]);
    const [exportSemicolonField, setExportSemicolonField] = useState<string>('');
    const [impersonatingUid, setImpersonatingUid] = useState<string | null>(null);
    const [organizations, setOrganizations] = useState<{ uid: string; name: string; abbreviation?: string }[]>([]);
    const [organizationsLoading, setOrganizationsLoading] = useState(false);
    const [organizationsError, setOrganizationsError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [highlightUid, setHighlightUid] = useState<string | null>(null);
    const tableContainerRef = useRef<HTMLDivElement | null>(null);

    const USERS_EXPORT_FIELDS = [
        { name: 'name', label: 'Name' },
        { name: 'email', label: 'Email' },
        { name: 'organization', label: 'Organization' },
        { name: 'active', label: 'Active' },
        { name: 'role', label: 'Role' },
        { name: 'collectionCount', label: 'Collections' },
        { name: 'collectionLimit', label: 'Collection limit' },
        { name: 'uid', label: 'UID' },
    ];

    const getExportableRow = useCallback((u: User): Record<string, unknown> => {
        const name = u.info?.name ||
            (u.firstName || u.lastName ? [u.firstName, u.lastName].filter(Boolean).join(' ') : '') ||
            u.uid || '';
        const accountRole = typeof u.role === 'string' && USER_ACCOUNT_ROLES.includes(u.role as UserAccountRole)
            ? (u.role as UserAccountRole)
            : resolvePeerAccountRole({ ...(u as Record<string, unknown>), role: u.role, admin: u.admin, info: u.info });
        return {
            name,
            email: u.email ?? '',
            organization: u.organization ?? '',
            active: u.active ?? true,
            role: accountRole,
            collectionCount: u.collectionCount ?? 0,
            collectionLimit: u.collectionLimit == null ? '' : u.collectionLimit === 0 ? 'Unlimited' : u.collectionLimit,
            uid: u.uid ?? '',
        };
    }, []);

    const stringifyCellForExport = useCallback((value: unknown): string => {
        if (value == null) return '';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    }, []);

    const getExportRows = useCallback((scope: 'all' | 'selection') => {
        if (scope === 'selection') return selectedUsers.length ? selectedUsers : [];
        return users;
    }, [users, selectedUsers]);

    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) return users;
        return users.filter((u) => userMatchesSearch(u, searchQuery));
    }, [users, searchQuery]);

    const runExport = useCallback((
        scope: 'all' | 'selection',
        format: 'json' | 'xlsx' | 'txt' | 'semicolon' | 'csv',
        fieldNames: string[]
    ) => {
        const rows = getExportRows(scope).map(getExportableRow);
        if (!rows.length) return;
        const nameToLabel = Object.fromEntries(USERS_EXPORT_FIELDS.map(f => [f.name, f.label]));
        const baseName = 'Users-export';

        if (format === 'json') {
            const dataForExport = rows.map(row => {
                const out: Record<string, unknown> = {};
                fieldNames.forEach(k => { out[nameToLabel[k] || k] = row[k]; });
                return out;
            });
            const blob = new Blob([JSON.stringify(dataForExport, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${baseName}.json`;
            a.click();
            URL.revokeObjectURL(url);
            return;
        }

        if (format === 'txt') {
            const headers = fieldNames.map(k => nameToLabel[k] || k);
            const lines = [headers.join('\t'), ...rows.map(row =>
                fieldNames.map(k => stringifyCellForExport(row[k])).join('\t')
            )];
            const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${baseName}.txt`;
            a.click();
            URL.revokeObjectURL(url);
            return;
        }

        if (format === 'csv') {
            const headers = fieldNames.map(k => nameToLabel[k] || k);
            const rowValues = rows.map(row =>
                fieldNames.map(k => stringifyCellForExport(row[k]))
            );

            const csvLines = [headers, ...rowValues].map(cells => joinCsvRow(cells));

            const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${baseName}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            return;
        }

        if (format === 'semicolon') {
            const key = fieldNames[0];
            if (!key) return;
            const line = rows.map(row => stringifyCellForExport(row[key])).join(';');
            const blob = new Blob([line], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${baseName}-semicolon.txt`;
            a.click();
            URL.revokeObjectURL(url);
            return;
        }

        if (format === 'xlsx') {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Users');
            const rowData = [
                fieldNames.map(k => nameToLabel[k] || k),
                ...rows.map(row => fieldNames.map(k => stringifyCellForExport(row[k])))
            ];
            rowData.forEach(row => worksheet.addRow(row));
            workbook.xlsx.writeBuffer().then((buffer: ArrayBuffer) => {
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${baseName}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
            });
        }
    }, [getExportRows, getExportableRow, stringifyCellForExport]);

    const openExportFieldDialog = useCallback((scope: 'all' | 'selection', format: 'json' | 'xlsx' | 'txt' | 'semicolon' | 'csv') => {
        const rows = getExportRows(scope);
        if (!rows.length && scope === 'selection') return;
        setExportParams({ scope, format });
        setExportAllFields(true);
        setExportSelectedFields([]);
        setExportSemicolonField(USERS_EXPORT_FIELDS[0]?.name ?? '');
        setExportDialogOpen(true);
    }, [getExportRows]);

    const confirmExport = useCallback(() => {
        if (!exportParams) return;
        const { scope, format } = exportParams;
        let fieldNames: string[];
        if (format === 'semicolon') {
            fieldNames = exportSemicolonField ? [exportSemicolonField] : [];
        } else {
            fieldNames = exportAllFields
                ? USERS_EXPORT_FIELDS.map(f => f.name)
                : exportSelectedFields;
        }
        if (!fieldNames.length) return;
        runExport(scope, format, fieldNames);
        setExportDialogOpen(false);
        setExportParams(null);
    }, [exportParams, exportAllFields, exportSelectedFields, exportSemicolonField, runExport]);

    const fetchUsers = useCallback(async () => {
        setIsLoadingUsers(true);
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const response = await fetch(`${API_BASE_URL}/users/peers`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to fetch users' }));
                throw new Error(errorData.message || `HTTP ${response.status}`);
            }

            const data = await response.json();
            const usersList: any[] = Array.isArray(data.peers) ? data.peers : (Array.isArray(data) ? data : []);

            const usersData: User[] = usersList.map((u: any) => {
                const firstName = u.firstName || u.info?.firstName || '';
                const lastName = u.lastName || u.info?.lastName || '';
                const name = u.name || (firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || '');
                const role = resolvePeerAccountRole({
                    role: u.role,
                    admin: u.admin ?? u.info?.admin,
                } as Record<string, unknown>);
                return {
                    uid: u.uid || u.id || u._id || '',
                    email: u.email || '',
                    active: u.active ?? u.info?.active ?? true,
                    admin: role === 'admin',
                    role,
                    firstName,
                    lastName,
                    organization: u.organization || u.info?.organization,
                    collectionLimit: u.collectionLimit ?? null,
                    collectionCount: u.collectionCount ?? 0,
                    userType: readUserType(u),
                    uiSettings: u.uiSettings && typeof u.uiSettings === 'object' ? u.uiSettings : undefined,
                    info: { ...u.info, name },
                };
            });

            setUsers(usersData.sort((a, b) => {
                const nameA = a.info?.name || `${a.firstName} ${a.lastName}`.trim() || a.uid || '';
                const nameB = b.info?.name || `${b.firstName} ${b.lastName}`.trim() || b.uid || '';
                return nameA.localeCompare(nameB);
            }));
        } catch (err: any) {
            console.error('Error fetching users:', err);
            setError(err.message || 'Failed to fetch users from API');
        } finally {
            setIsLoadingUsers(false);
        }
    }, []);

    useEffect(() => {
        if (user?.admin || user?.info?.admin) {
            fetchUsers();
        } else {
            setIsLoadingUsers(false);
        }
    }, [user?.admin, user?.info?.admin, fetchUsers]);

    useEffect(() => {
        const loadOrganizations = async () => {
            try {
                setOrganizationsLoading(true);
                setOrganizationsError(null);
                const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
                const response = await fetch(`${API_BASE_URL}/tenants`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                });
                if (!response.ok) {
                    const data = await response.json().catch(() => null);
                    throw new Error(data?.message || `Failed to load organizations (HTTP ${response.status})`);
                }
                const data = await response.json().catch(() => null);
                const list: any[] = Array.isArray(data?.tenants) ? data.tenants : [];
                const mapped = list
                    .map((t) => ({
                        uid: t.uid || t.id || '',
                        name: typeof t.name === 'string' ? t.name : '',
                        abbreviation: typeof t.abbreviation === 'string' ? t.abbreviation : undefined,
                    }))
                    .filter((t) => t.uid && t.name);
                setOrganizations(mapped);
            } catch (err: any) {
                console.error('Error loading organizations:', err);
                setOrganizationsError(err.message || 'Failed to load organizations');
            } finally {
                setOrganizationsLoading(false);
            }
        };
        loadOrganizations().catch(() => undefined);
    }, []);

    useEffect(() => {
        const uidFromUrl = searchParams.get('uid')?.trim();
        if (!uidFromUrl) return;

        if (!users || users.length === 0) {
            return;
        }

        const clearUidParam = () => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('uid');
                return next;
            }, { replace: true });
        };

        const found = users.find((u) => u.uid === uidFromUrl);
        if (found) {
            setSearchQuery('');
            setHighlightUid(uidFromUrl);
            setError(null);
            clearUidParam();
            return;
        }

        setError('User not found');
        clearUidParam();
    }, [searchParams, users, setSearchParams]);

    useEffect(() => {
        if (!highlightUid) return;
        const el = tableContainerRef.current?.querySelector<HTMLElement>(
            `[data-row-uid="${CSS.escape(highlightUid)}"]`
        );
        if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [highlightUid, filteredUsers.length]);

    const handleSetLimitClick = (targetUser: User) => {
        setSelectedUserForLimit(targetUser);
        const currentLimit = targetUser.collectionLimit;
        if (currentLimit == null) {
            setCollectionLimitInput('');
        } else {
            setCollectionLimitInput(currentLimit === 0 ? '0' : String(currentLimit));
        }
        setLimitDialogOpen(true);
        setError(null);
    };

    const handleInterfaceClick = (targetUser: User) => {
        setSelectedUserForUi(targetUser);
        setUiSettingsForm(uiSettingsFromUser(targetUser));
        setUserTypeForm(readUserType(targetUser));
        setUiDialogOpen(true);
        setError(null);
    };

    const handleUpdateUiSettings = async () => {
        if (!selectedUserForUi?.uid) return;

        setError(null);
        setIsUpdatingUi(true);

        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const response = await fetch(`${API_BASE_URL}/auth/admin/user/${selectedUserForUi.uid}/ui-settings`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ ...uiSettingsForm, userType: userTypeForm }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to update interface settings' }));
                throw new Error(errorData.message || 'Failed to update interface settings');
            }

            const data = await response.json().catch(() => ({}));
            const nextSettings = data?.user?.uiSettings ?? uiSettingsForm;
            const nextUserType = readUserType({ userType: data?.user?.userType ?? userTypeForm });
            setUsers(prev => prev.map(u => {
                if (u.uid !== selectedUserForUi.uid) return u;
                return { ...u, uiSettings: nextSettings, userType: nextUserType };
            }));

            if (selectedUserForUi.uid === user?.uid) {
                await refreshUser();
            }

            setUiDialogOpen(false);
            setSelectedUserForUi(null);
        } catch (err: any) {
            console.error('Error updating interface settings:', err);
            setError(err.message || 'Failed to update interface settings');
        } finally {
            setIsUpdatingUi(false);
        }
    };

    const handleUpdateCollectionLimit = async () => {
        if (!selectedUserForLimit?.uid) return;

        setError(null);
        setIsUpdatingLimit(true);

        try {
            const limitValue = collectionLimitInput.trim() === '' || collectionLimitInput === 'unlimited'
                ? 0
                : parseInt(collectionLimitInput, 10);

            if (isNaN(limitValue) || limitValue < 0) {
                throw new Error('Please enter 0 for unlimited or a positive number');
            }

            const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const response = await fetch(`${API_BASE_URL}/auth/admin/user/${selectedUserForLimit.uid}/collection-limit`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ collectionLimit: limitValue }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to update collection limit' }));
                throw new Error(errorData.message || 'Failed to update collection limit');
            }

            setUsers(prev => prev.map(u => {
                if (u.uid !== selectedUserForLimit.uid) return u;
                return {
                    ...u,
                    collectionLimit: limitValue,
                    collectionCount: u.collectionCount ?? 0,
                };
            }));

            setLimitDialogOpen(false);
            setSelectedUserForLimit(null);
            setCollectionLimitInput('');
        } catch (err: any) {
            console.error('Error updating collection limit:', err);
            setError(err.message || 'Failed to update collection limit');
        } finally {
            setIsUpdatingLimit(false);
        }
    };

    const handleDeleteClick = (targetUser: User) => {
        if (targetUser.uid === user?.uid) return;
        setUserToDelete(targetUser);
        setDeleteDialogOpen(true);
        setError(null);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete?.uid) return;
        if (userToDelete.uid === user?.uid) {
            setError('You cannot delete your own account.');
            return;
        }
        setIsDeleting(true);
        setError(null);
        try {
            await deleteItemAPI(USERS_SLUG, userToDelete.uid);
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            await fetchUsers();
        } catch (err: any) {
            console.error('Error deleting user:', err);
            setError(err.message || 'Failed to delete user');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleEditClick = (targetUser: User) => {
        setUserToEdit(targetUser);
        setEditForm({
            firstName: targetUser.firstName ?? '',
            lastName: targetUser.lastName ?? '',
            email: targetUser.email ?? '',
            organization: targetUser.organization ?? '',
            active: targetUser.active ?? true,
            role: resolvePeerAccountRole({
                role: targetUser.role,
                admin: targetUser.admin,
                info: targetUser.info,
            } as Record<string, unknown>),
        });
        setEditDialogOpen(true);
        setError(null);
    };

    const handleEditSave = async () => {
        if (!userToEdit?.uid) return;
        setIsSavingEdit(true);
        setError(null);
        try {
            const res = await dataAPI.getCollectionItem(USERS_SLUG, userToEdit.uid);
            const existing = (res?.data || res) as Record<string, unknown>;
            const payload = {
                ...existing,
                firstName: editForm.firstName,
                lastName: editForm.lastName,
                email: editForm.email,
                organization: editForm.organization,
                active: editForm.active,
                role: editForm.role,
                admin: editForm.role === 'admin',
            };
            await updateItemAPI(USERS_SLUG, userToEdit.uid, payload);
            setEditDialogOpen(false);
            setUserToEdit(null);
            await fetchUsers();
        } catch (err: any) {
            console.error('Error updating user:', err);
            setError(err.message || 'Failed to update user');
        } finally {
            setIsSavingEdit(false);
        }
    };

    const handleCreateClick = () => {
        setCreateForm({
            firstName: '',
            lastName: '',
            email: '',
            organization: '',
            active: true,
            role: 'user',
        });
        setCreateDialogOpen(true);
        setError(null);
    };

    const handleCreateSave = async () => {
        setIsSavingCreate(true);
        setError(null);
        try {
            if (!createForm.email.trim()) {
                throw new Error('Email is required');
            }
            if (!createForm.firstName.trim()) {
                throw new Error('First name is required');
            }
            if (!createForm.lastName.trim()) {
                throw new Error('Last name is required');
            }

            await authAPI.createAdminUser({
                email: createForm.email.trim(),
                firstName: createForm.firstName.trim(),
                lastName: createForm.lastName.trim(),
                organization: createForm.organization.trim() || undefined,
                active: createForm.active,
                role: createForm.role,
            });

            setCreateDialogOpen(false);
            await fetchUsers();
        } catch (err: any) {
            console.error('Error creating user:', err);
            setError(err.message || 'Failed to create user');
        } finally {
            setIsSavingCreate(false);
        }
    };

    const handleImpersonate = async (targetUser: User) => {
        const uid = targetUser?.uid;
        if (!uid) {
            setError('Invalid user');
            return;
        }
        setImpersonatingUid(uid);
        setError(null);
        try {
            await impersonateUserByUid(uid);
            return;
        } catch (err: any) {
            console.error('Error impersonating user:', err);
            setError(err.message || 'Failed to impersonate user');
        } finally {
            setImpersonatingUid(null);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Users</h1>
                    <p className="text-gray-600 dark:text-gray-400">Manage users and collection limits</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button onClick={handleCreateClick} className="flex items-center space-x-2">
                        <UserPlus className="w-4 h-4" />
                        <span>Add user</span>
                    </Button>
                    {users.length > 0 && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="flex items-center space-x-2">
                                    <Download className="w-4 h-4" />
                                    <span className="hidden sm:inline">Export</span>
                                    <ChevronDown className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>All</DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                        <DropdownMenuItem onClick={() => openExportFieldDialog('all', 'json')}>As JSON</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openExportFieldDialog('all', 'xlsx')}>As xlsx</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openExportFieldDialog('all', 'txt')}>As txt</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openExportFieldDialog('all', 'csv')}>As CSV</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openExportFieldDialog('all', 'semicolon')}>As semi-colon separated</DropdownMenuItem>
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>Selection</DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                        <DropdownMenuItem onClick={() => openExportFieldDialog('selection', 'json')} disabled={selectedUsers.length === 0}>As JSON</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openExportFieldDialog('selection', 'xlsx')} disabled={selectedUsers.length === 0}>As xlsx</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openExportFieldDialog('selection', 'txt')} disabled={selectedUsers.length === 0}>As txt</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openExportFieldDialog('selection', 'csv')} disabled={selectedUsers.length === 0}>As CSV</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openExportFieldDialog('selection', 'semicolon')} disabled={selectedUsers.length === 0}>As semi-colon separated</DropdownMenuItem>
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>

            <Card>
                <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle>Users</CardTitle>
                    {users.length > 0 ? (
                        <div className="relative w-full sm:max-w-sm">
                            <Search className={searchInputOverlayIconClass()} aria-hidden />
                            <Input
                                type="search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search users..."
                                className="pl-9"
                                aria-label="Search users"
                            />
                        </div>
                    ) : null}
                </CardHeader>
                <CardContent>
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                    {isLoadingUsers ? (
                        <div className="flex items-center justify-center p-8">
                            <div className="text-center">
                                <Loader2 className="w-10 h-10 mx-auto text-gray-400 animate-spin mb-4" />
                                <p className="text-gray-500 dark:text-gray-400">Loading users...</p>
                            </div>
                        </div>
                    ) : users.length === 0 && !error ? (
                        <p className="text-gray-500 dark:text-gray-400">No users found. Click Add user to create one.</p>
                    ) : filteredUsers.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400">No users match your search</p>
                    ) : (
                        <div ref={tableContainerRef} className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-10">
                                            <Checkbox
                                                checked={
                                                    filteredUsers.length === 0 || selectedUsers.length === 0 ? false :
                                                        selectedUsers.length === filteredUsers.length &&
                                                            filteredUsers.every((u) => selectedUsers.some((s) => s.uid === u.uid))
                                                            ? true
                                                            : 'indeterminate'
                                                }
                                                onCheckedChange={(checked) => {
                                                    if (checked) {
                                                        setSelectedUsers((prev) => {
                                                            const merged = [...prev, ...filteredUsers];
                                                            return merged.filter((v, i, a) => a.findIndex((x) => x.uid === v.uid) === i);
                                                        });
                                                    } else {
                                                        const visibleUids = new Set(filteredUsers.map((u) => u.uid));
                                                        setSelectedUsers((prev) => prev.filter((u) => !visibleUids.has(u.uid)));
                                                    }
                                                }}
                                                aria-label="Select all visible"
                                            />
                                        </TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Organization</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Role</TableHead>
                                        <TableHead>Collections</TableHead>
                                        <TableHead>Collection limit</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredUsers.map((targetUser) => {
                                        const displayName = getUserDisplayName(targetUser);
                                        const isHighlighted = highlightUid === targetUser.uid;

                                        const collectionLimit = targetUser.collectionLimit;
                                        const collectionCount = targetUser.collectionCount ?? 0;

                                        let collectionDisplay = '-';
                                        if (collectionLimit === null) {
                                            collectionDisplay = `${collectionCount} (not set)`;
                                        } else if (collectionLimit === 0) {
                                            collectionDisplay = `${collectionCount} (unlimited)`;
                                        } else {
                                            collectionDisplay = `${collectionCount}/${collectionLimit}`;
                                        }

                                        const limitDisplay = collectionLimit === null
                                            ? 'Not set'
                                            : collectionLimit === 0
                                                ? 'Unlimited'
                                                : String(collectionLimit);

                                        const isSelected = selectedUsers.some(u => u.uid === targetUser.uid);
                                        const isOwnUser = targetUser.uid === user?.uid;
                                        return (
                                            <ContextMenu key={targetUser.uid}>
                                                <ContextMenuTrigger asChild>
                                                    <TableRow
                                                        data-row-uid={targetUser.uid}
                                                        data-highlight={isHighlighted ? 'true' : undefined}
                                                        className={isHighlighted
                                                            ? 'bg-amber-50 dark:bg-amber-900/40 border-l-4 border-amber-400 dark:border-amber-300 transition-colors duration-1000 ease-out'
                                                            : undefined}
                                                    >
                                                        <TableCell className="w-10">
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onCheckedChange={(checked) => {
                                                                    if (checked) setSelectedUsers(prev => [...prev, targetUser].filter((v, i, a) => a.findIndex(x => x.uid === v.uid) === i));
                                                                    else setSelectedUsers(prev => prev.filter(u => u.uid !== targetUser.uid));
                                                                }}
                                                                aria-label="Select row"
                                                            />
                                                        </TableCell>
                                                        <TableCell className="font-medium">{displayName}</TableCell>
                                                        <TableCell>{targetUser.email || '-'}</TableCell>
                                                        <TableCell>{targetUser.organization || '-'}</TableCell>
                                                        <TableCell>
                                                            {targetUser.active ? (
                                                                <span className="text-green-600 dark:text-green-400">Active</span>
                                                            ) : (
                                                                <span className="text-red-600 dark:text-red-400">Inactive</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="capitalize text-muted-foreground">
                                                            {resolvePeerAccountRole({
                                                                role: targetUser.role,
                                                                admin: targetUser.admin,
                                                                info: targetUser.info,
                                                            } as Record<string, unknown>)}
                                                        </TableCell>
                                                        <TableCell>{collectionDisplay}</TableCell>
                                                        <TableCell>{limitDisplay}</TableCell>
                                                        <TableCell>
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        className="h-8"
                                                                    >
                                                                        <MoreHorizontal className="w-4 h-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem onClick={() => handleEditClick(targetUser)}>
                                                                        <Pencil className="w-4 h-4 mr-2" />
                                                                        Edit
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem onClick={() => handleSetLimitClick(targetUser)}>
                                                                        <Sliders className="w-4 h-4 mr-2" />
                                                                        Set collection limit
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem onClick={() => handleInterfaceClick(targetUser)}>
                                                                        <EyeOff className="w-4 h-4 mr-2" />
                                                                        Interface
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        onClick={() => handleImpersonate(targetUser)}
                                                                        disabled={isOwnUser || impersonatingUid !== null}
                                                                    >
                                                                        <UserCog className="w-4 h-4 mr-2" />
                                                                        {impersonatingUid === targetUser.uid ? 'Impersonating...' : 'Impersonate'}
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuSeparator />
                                                                    <DropdownMenuItem
                                                                        onClick={() => handleDeleteClick(targetUser)}
                                                                        disabled={isOwnUser}
                                                                        className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                                                                    >
                                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                                        Delete
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </TableCell>
                                                    </TableRow>
                                                </ContextMenuTrigger>
                                                <ContextMenuContent>
                                                    <ContextMenuItem onClick={() => handleEditClick(targetUser)}>
                                                        <Pencil className="w-4 h-4 mr-2" />
                                                        Edit
                                                    </ContextMenuItem>
                                                    <ContextMenuItem onClick={() => handleSetLimitClick(targetUser)}>
                                                        <Sliders className="w-4 h-4 mr-2" />
                                                        Set collection limit
                                                    </ContextMenuItem>
                                                    <ContextMenuItem onClick={() => handleInterfaceClick(targetUser)}>
                                                        <EyeOff className="w-4 h-4 mr-2" />
                                                        Interface
                                                    </ContextMenuItem>
                                                    <ContextMenuItem
                                                        onClick={() => handleImpersonate(targetUser)}
                                                        disabled={isOwnUser || impersonatingUid !== null}
                                                    >
                                                        <UserCog className="w-4 h-4 mr-2" />
                                                        {impersonatingUid === targetUser.uid ? 'Impersonating...' : 'Impersonate'}
                                                    </ContextMenuItem>
                                                    <ContextMenuSeparator />
                                                    <ContextMenuItem
                                                        onClick={() => handleDeleteClick(targetUser)}
                                                        disabled={isOwnUser}
                                                        className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Delete
                                                    </ContextMenuItem>
                                                </ContextMenuContent>
                                            </ContextMenu>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={limitDialogOpen} onOpenChange={(open) => {
                setLimitDialogOpen(open);
                if (!open) {
                    setSelectedUserForLimit(null);
                    setCollectionLimitInput('');
                    setError(null);
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Set Collection Limit</DialogTitle>
                        <DialogDescription>
                            Set the collection limit for {selectedUserForLimit?.info?.name || selectedUserForLimit?.email || 'this user'}.
                            Enter 0 for unlimited, or a positive number for a specific limit.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="collectionLimit">Collection Limit</Label>
                            <Input
                                id="collectionLimit"
                                type="number"
                                min="0"
                                value={collectionLimitInput}
                                onChange={(e) => setCollectionLimitInput(e.target.value)}
                                placeholder="0 for unlimited, or enter a number"
                            />
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Current: {selectedUserForLimit?.collectionLimit === null
                                    ? 'Not set'
                                    : selectedUserForLimit?.collectionLimit === 0
                                        ? 'Unlimited'
                                        : selectedUserForLimit?.collectionLimit}
                                {' '}
                                ({selectedUserForLimit?.collectionCount ?? 0} collections created)
                            </p>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setLimitDialogOpen(false);
                                    setSelectedUserForLimit(null);
                                    setCollectionLimitInput('');
                                    setError(null);
                                }}
                                disabled={isUpdatingLimit}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleUpdateCollectionLimit}
                                disabled={!collectionLimitInput || isUpdatingLimit}
                            >
                                {isUpdatingLimit ? 'Updating...' : 'Update Limit'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={uiDialogOpen} onOpenChange={(open) => {
                setUiDialogOpen(open);
                if (!open) {
                    setSelectedUserForUi(null);
                    setError(null);
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Interface</DialogTitle>
                        <DialogDescription>
                            Set the dashboard and hide parts of the app for {selectedUserForUi ? getUserDisplayName(selectedUserForUi) : 'this user'}.
                            Data stays in place. The latest save wins.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                        <UserTypeField
                            id="admin-user-type"
                            value={userTypeForm}
                            onChange={setUserTypeForm}
                            disabled={isUpdatingUi}
                        />
                        <UiSettingsFields
                            idPrefix="admin-ui"
                            value={uiSettingsForm}
                            onChange={setUiSettingsForm}
                            disabled={isUpdatingUi}
                        />
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setUiDialogOpen(false);
                                    setSelectedUserForUi(null);
                                    setError(null);
                                }}
                                disabled={isUpdatingUi}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleUpdateUiSettings} disabled={isUpdatingUi}>
                                {isUpdatingUi ? 'Saving...' : 'Save interface'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
                setDeleteDialogOpen(open);
                if (!open) setUserToDelete(null);
            }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete user</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete {userToDelete?.info?.name || userToDelete?.email || 'this user'}?
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            disabled={isDeleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={createDialogOpen} onOpenChange={(open) => {
                setCreateDialogOpen(open);
                if (!open) {
                    setError(null);
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add user</DialogTitle>
                        <DialogDescription>
                            Create a new user account. They can sign in via magic link unless this is the demo account.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="createFirstName">First name</Label>
                                <Input
                                    id="createFirstName"
                                    value={createForm.firstName}
                                    onChange={(e) => setCreateForm(f => ({ ...f, firstName: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="createLastName">Last name</Label>
                                <Input
                                    id="createLastName"
                                    value={createForm.lastName}
                                    onChange={(e) => setCreateForm(f => ({ ...f, lastName: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="createEmail">Email</Label>
                            <Input
                                id="createEmail"
                                type="email"
                                value={createForm.email}
                                onChange={(e) => setCreateForm(f => ({ ...f, email: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="createOrganization">Organization</Label>
                            <Select
                                value={createForm.organization || '__none'}
                                onValueChange={(value) =>
                                    setCreateForm((f) => ({ ...f, organization: value === '__none' ? '' : value }))
                                }
                            >
                                <SelectTrigger id="createOrganization">
                                    <SelectValue placeholder="Select organization" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none">No organization</SelectItem>
                                    {organizations.map((org) => (
                                        <SelectItem key={org.uid} value={org.abbreviation || org.name}>
                                            {org.name}
                                            {org.abbreviation ? ` (${org.abbreviation})` : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center space-x-6">
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="createActive"
                                    checked={createForm.active}
                                    onCheckedChange={(checked) => setCreateForm(f => ({ ...f, active: checked === true }))}
                                />
                                <Label htmlFor="createActive">Active</Label>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="createRole">Role</Label>
                                <Select
                                    value={createForm.role}
                                    onValueChange={(value: UserAccountRole) =>
                                        setCreateForm((f) => ({ ...f, role: value }))
                                    }
                                >
                                    <SelectTrigger id="createRole">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {USER_ACCOUNT_ROLES.map((r) => (
                                            <SelectItem key={r} value={r}>
                                                {r}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => { setCreateDialogOpen(false); setError(null); }}
                                disabled={isSavingCreate}
                            >
                                Cancel
                            </Button>
                            <Button onClick={() => void handleCreateSave()} disabled={isSavingCreate}>
                                {isSavingCreate ? 'Creating...' : 'Create user'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={editDialogOpen} onOpenChange={(open) => {
                setEditDialogOpen(open);
                if (!open) {
                    setUserToEdit(null);
                    setError(null);
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit user</DialogTitle>
                        <DialogDescription>
                            Update user details for {userToEdit?.info?.name || userToEdit?.email || 'this user'}.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="editFirstName">First name</Label>
                                <Input
                                    id="editFirstName"
                                    value={editForm.firstName}
                                    onChange={(e) => setEditForm(f => ({ ...f, firstName: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="editLastName">Last name</Label>
                                <Input
                                    id="editLastName"
                                    value={editForm.lastName}
                                    onChange={(e) => setEditForm(f => ({ ...f, lastName: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="editEmail">Email</Label>
                            <Input
                                id="editEmail"
                                type="email"
                                value={editForm.email}
                                onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="editOrganization">Organization</Label>
                            <Select
                                value={editForm.organization || '__none'}
                                onValueChange={(value) =>
                                    setEditForm((f) => ({ ...f, organization: value === '__none' ? '' : value }))
                                }
                            >
                                <SelectTrigger id="editOrganization">
                                    <SelectValue placeholder="Select organization" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none">No organization</SelectItem>
                                    {editForm.organization &&
                                        !organizations.some(
                                            (o) =>
                                                o.name === editForm.organization ||
                                                o.abbreviation === editForm.organization
                                        ) && (
                                            <SelectItem value={editForm.organization}>
                                                Current: {editForm.organization}
                                            </SelectItem>
                                        )}
                                    {organizations.map((org) => (
                                        <SelectItem key={org.uid} value={org.abbreviation || org.name}>
                                            {org.name}
                                            {org.abbreviation ? ` (${org.abbreviation})` : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {organizationsLoading && (
                                <p className="text-xs text-gray-500">Loading organizations...</p>
                            )}
                            {organizationsError && (
                                <p className="text-xs text-red-600">{organizationsError}</p>
                            )}
                        </div>
                        <div className="flex items-center space-x-6">
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="editActive"
                                    checked={editForm.active}
                                    onCheckedChange={(checked) => setEditForm(f => ({ ...f, active: checked === true }))}
                                />
                                <Label htmlFor="editActive">Active</Label>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="editRole">Role</Label>
                                <Select
                                    value={editForm.role}
                                    onValueChange={(value: UserAccountRole) =>
                                        setEditForm((f) => ({ ...f, role: value }))
                                    }
                                >
                                    <SelectTrigger id="editRole">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {USER_ACCOUNT_ROLES.map((r) => (
                                            <SelectItem key={r} value={r}>
                                                {r}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => { setEditDialogOpen(false); setUserToEdit(null); setError(null); }}
                                disabled={isSavingEdit}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleEditSave} disabled={isSavingEdit}>
                                {isSavingEdit ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Export options</DialogTitle>
                        <DialogDescription>
                            {exportParams?.format === 'semicolon'
                                ? 'Semi-colon separated export allows one field only. Choose the field to export.'
                                : 'Choose whether to include all fields or select certain fields in the export.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {exportParams?.format === 'semicolon' ? (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Field</label>
                                <Select value={exportSemicolonField} onValueChange={setExportSemicolonField}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select one field" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {USERS_EXPORT_FIELDS.map(f => (
                                            <SelectItem key={f.name} value={f.name}>{f.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <>
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Fields to export</label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="exportFields"
                                                checked={exportAllFields}
                                                onChange={() => setExportAllFields(true)}
                                                className="rounded-full border-gray-300"
                                            />
                                            <span className="text-sm">All fields</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="exportFields"
                                                checked={!exportAllFields}
                                                onChange={() => setExportAllFields(false)}
                                                className="rounded-full border-gray-300"
                                            />
                                            <span className="text-sm">Select certain fields</span>
                                        </label>
                                    </div>
                                </div>
                                {!exportAllFields && (
                                    <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-2">
                                        {USERS_EXPORT_FIELDS.map(f => (
                                            <label key={f.name} className="flex items-center gap-2 cursor-pointer">
                                                <Checkbox
                                                    checked={exportSelectedFields.includes(f.name)}
                                                    onCheckedChange={(checked) => {
                                                        setExportSelectedFields(prev =>
                                                            checked ? [...prev, f.name] : prev.filter(x => x !== f.name)
                                                        );
                                                    }}
                                                />
                                                <span className="text-sm">{f.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmExport}
                            disabled={
                                exportParams?.format === 'semicolon'
                                    ? !exportSemicolonField
                                    : !exportAllFields && exportSelectedFields.length === 0
                            }
                        >
                            Export
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminUsersPage;
